import { captureException } from '@sentry/react';
import { sdClient } from 'contexts/SDKClientContext';
import { ReactNode, useMemo } from 'react';
import {
    createZustandContext,
    getZustandStoreHook,
    ZustandContextInitializer,
    ZustandContextProvider,
} from 'shared/zustand_context/zustand_context';
import { getErrorStatus } from 'utils/api';
import { PersistStorage, persist, StorageValue } from 'zustand/middleware';
import type { UserSettings, UserSettingsActions } from './types';
import { DefaultQuickFilters } from 'pages/workbench/side_panel/indicator_filter/shared';

const UserSettingsStorage: PersistStorage<UserSettings> = {
    getItem: async (name): Promise<StorageValue<UserSettings> | null> => {
        const remotePromise = sdClient.getSelfSdWebUserSettings({});

        const raw = localStorage.getItem(name);
        const local: StorageValue<UserSettings> | null = raw ? JSON.parse(raw) : null;
        const localUpdated = localStorage.getItem(`${name}:updated`);

        try {
            const res = await remotePromise;

            if (!localUpdated || res.Updated > new Date(localUpdated)) {
                const winner = { state: res.Data };
                localStorage.setItem(name, JSON.stringify(winner));
                localStorage.setItem(`${name}:updated`, res.Updated.toISOString());
                return winner;
            }
        } catch (err) {
            // a 404 simply means that the user has not configured any settings
            if (getErrorStatus(err) !== 404) {
                captureException(err, {
                    tags: { codeLocation: 'UserSettingsStore.getItem' },
                });
            }
        }

        return local;
    },

    setItem: (name, value: StorageValue<UserSettings>) => {
        const updated = new Date().toISOString();
        localStorage.setItem(name, JSON.stringify(value));
        localStorage.setItem(`${name}:updated`, updated);
        sdClient.updateSelfSdWebUserSettings({ body: value.state }).catch((err) => {
            captureException(err, {
                tags: { codeLocation: 'UserSettingsStore.setItem - remote' },
            });
        });
    },

    removeItem: (name) => {
        localStorage.removeItem(name);
        localStorage.removeItem(`${name}:updated`);
        sdClient.updateSelfSdWebUserSettings({ body: null }).catch((err) => {
            captureException(err, {
                tags: { codeLocation: 'UserSettingsStore.removeItem - remote' },
            });
        });
    },
};

export const UserSettingsContext = createZustandContext<
    UserSettings & UserSettingsActions
>();

export const useUserSettingsStore = getZustandStoreHook(UserSettingsContext);

const getUserSettingsStateInitializer = (
    overrides: Partial<UserSettings> = {},
): ZustandContextInitializer<UserSettings & UserSettingsActions> => {
    return persist(
        (set, _get) => ({
            envSwitcher: undefined,
            quickFilters: DefaultQuickFilters,
            ...overrides,
            setEnvSwitcherEnabled: (enabled) => set(() => ({ envSwitcher: enabled })),
            setQuickFilters: (quickFilters) => set(() => ({ quickFilters })),
        }),
        {
            name: 'user-settings',
            storage: UserSettingsStorage,
            partialize: (s) => ({
                envSwitcher: s.envSwitcher,
                quickFilters: s.quickFilters,
            }),
        },
    ) as ZustandContextInitializer<UserSettings & UserSettingsActions>;
};

export type UserSettingsProviderProps = Readonly<{
    children: ReactNode;
    overrides?: Partial<UserSettings>;
}>;

export function UserSettingsProvider({ children, overrides }: UserSettingsProviderProps) {
    const initializer = useMemo(
        () => getUserSettingsStateInitializer(overrides),
        [overrides],
    );

    return (
        <ZustandContextProvider ctx={UserSettingsContext} initializer={initializer}>
            {children}
        </ZustandContextProvider>
    );
}
